export type ImportFileKind =
  | 'Unknown'
  | 'Staff'
  | 'Classes'
  | 'Families'
  | 'Students'
  | 'Enrollments'

export type ImportEntity =
  | 'Staff'
  | 'Class'
  | 'Household'
  | 'Guardian'
  | 'Dancer'
  | 'Enrollment'

export type ChangeAction =
  | 'Create'
  | 'Update'
  | 'Unchanged'
  | 'Skipped'
  | 'Unmatched'
  | 'OnlyHere'

export type DetectedFile = {
  fileName: string
  kind: ImportFileKind
  rowCount: number
  problem: string | null
}

export type ImportChange = {
  entity: ImportEntity
  action: ChangeAction
  label: string
  detail: string | null
}

export type ImportEntitySummary = {
  entity: ImportEntity
  created: number
  updated: number
  unchanged: number
  skipped: number
  unmatched: number
  onlyHere: number
  total: number
}

/**
 * What became of every row in one file.
 *
 * The numbers have to add up on screen. 855 student rows producing 735 dancers
 * is a 120-row gap somebody would rightly ask about, and "trust me" is not an
 * answer when the alternative is opening Jackrabbit and counting.
 */
export type RowAccount = {
  kind: ImportFileKind
  read: number
  /** Rows that became something. */
  used: number
  /** The CDATEAM billing shadow — a second account for competition fees. */
  shadow: number
  /** Read, understood, and deliberately not imported: inactive classes, fee rows. */
  excluded: number
  /** Missing something required, so reported rather than guessed at. */
  incomplete: number
}

export type ImportPlan = {
  files: DetectedFile[]
  summary: ImportEntitySummary[]
  changes: ImportChange[]
  needsAttention: ImportChange[]
  warnings: string[]
  /** What became of every row read, per file. */
  rows: RowAccount[]
  applied: boolean
  /** Dated sessions generated from the class definitions (A-1). */
  sessionsScheduled: number
  /**
   * What became of the registration links, refreshed as part of applying
   * (D-172). Null on a preview, which never reaches the network.
   */
  registrationLinks: RegistrationLinkOutcome | null
}

export type RegistrationLinkOutcome = {
  refreshed: boolean
  changed: number
  /** A sentence for the screen, or null when there is nothing to say. */
  problem: string | null
}

/** Rows neither used nor explained. Always zero unless the importer has a bug. */
export function unaccounted(row: RowAccount): number {
  return row.read - row.used - row.shadow - row.excluded - row.incomplete
}

/**
 * The parts of a row account worth showing, in the order they are asked about.
 *
 * Zeroes are dropped: a studio with no billing shadow should not be shown a
 * "Shadow 0" chip inviting somebody to wonder what it means.
 */
export function accountParts(row: RowAccount): { label: string; count: number; hint: string }[] {
  return [
    { label: 'imported', count: row.used, hint: 'Became a record in the app' },
    {
      label: 'team billing',
      count: row.shadow,
      hint: 'The CDATEAM account, which exists to bill competition fees through the non-profit',
    },
    {
      label: 'not imported',
      count: row.excluded,
      hint: 'Inactive or cancelled, or a registration or team fee Jackrabbit files as an enrolment',
    },
    {
      label: 'needs a fix',
      count: row.incomplete,
      hint: 'Missing something required — listed under "Worth a look"',
    },
  ].filter((p) => p.count > 0)
}

/**
 * Sends the files up, either to be described or to be applied.
 *
 * One call for both, matching the server: what the preview describes is what
 * apply performs, because it is the same run with a flag flipped.
 */
export async function runImport(files: File[], apply: boolean): Promise<ImportPlan> {
  const body = new FormData()
  for (const file of files) body.append('files', file, file.name)
  body.append('apply', String(apply))

  const res = await fetch('/api/imports', { method: 'POST', body })

  if (!res.ok) {
    // The server sends ProblemDetails for every refusal it can explain.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Import failed (${res.status})`)
  }

  return res.json()
}

export const ENTITY_LABELS: Record<ImportEntity, string> = {
  Staff: 'Staff',
  Class: 'Classes',
  Household: 'Families',
  Guardian: 'Parents',
  Dancer: 'Dancers',
  Enrollment: 'Enrolments',
}

export const FILE_KIND_LABELS: Record<ImportFileKind, string> = {
  Unknown: 'Not recognised',
  Staff: 'Staff list',
  Classes: 'Class search',
  Families: 'Family list',
  Students: 'Student list',
  Enrollments: 'Enrolment detail',
}

/** Every export Jackrabbit produces that this understands. */
export const EXPECTED_KINDS: ImportFileKind[] = [
  'Staff',
  'Families',
  'Students',
  'Classes',
  'Enrollments',
]

/**
 * Which exports are missing from a dropped set.
 *
 * Worth saying out loud rather than leaving to be inferred from a short list.
 * The files are told apart by their columns, not their names (they arrive as
 * `ListFamiliesResultsExport (2).xls`), so "I only sent three" and "one of the
 * four wasn't recognised" look identical until this is spelled out.
 */
export function missingKinds(files: DetectedFile[]): ImportFileKind[] {
  const present = new Set(files.map((f) => f.kind))
  return EXPECTED_KINDS.filter((kind) => !present.has(kind))
}

/**
 * Whether anything would actually change.
 *
 * Re-running an unchanged export is the *expected* case while Jackrabbit stays
 * the system of record, so a run that does nothing is a success worth stating
 * plainly rather than an empty screen.
 */
export function hasWork(plan: ImportPlan): boolean {
  return plan.summary.some((s) => s.created > 0 || s.updated > 0)
}

export function totalOf(plan: ImportPlan, field: 'created' | 'updated' | 'unchanged'): number {
  return plan.summary.reduce((sum, s) => sum + s[field], 0)
}

/**
 * Groups changes under their entity, in the order the summary lists them.
 *
 * Rows arrive interleaved because the importer works family-then-dancer, and a
 * flat list of 1,300 mixed rows is unreadable.
 */
export function groupChanges(changes: ImportChange[]): { entity: ImportEntity; items: ImportChange[] }[] {
  const order: ImportEntity[] = ['Staff', 'Class', 'Household', 'Guardian', 'Dancer', 'Enrollment']

  return order
    .map((entity) => ({ entity, items: changes.filter((c) => c.entity === entity) }))
    .filter((group) => group.items.length > 0)
}

export type RegistrationLinkResult = {
  listed: number
  matched: number
  changed: number
  /** Listed by Jackrabbit, not recognised here. Reported rather than guessed at. */
  unmatched: string[]
}

/**
 * Re-reads Jackrabbit's public class listing for their class ids (D-112).
 *
 * Separate from the import because it is a different kind of act — that reads
 * files someone chose, this reaches across the network to a page we do not
 * control. Failing to reach them must not read as a failed import.
 */
export async function refreshRegistrationLinks(): Promise<RegistrationLinkResult> {
  const res = await fetch('/api/imports/registration-links', { method: 'POST' })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not refresh the links (${res.status})`)
  }

  return res.json()
}

/**
 * Both spreadsheet formats are accepted.
 *
 * The four grid exports arrive as `.xlsx` and the enrolment report as legacy
 * BIFF8 `.xls` (D-143), so refusing either would refuse half the contract. The
 * reader identifies a workbook by its header rather than its extension, which
 * also survives a file renamed on somebody's desktop.
 */
export function rejectReason(file: File): string | null {
  const name = file.name.toLowerCase()
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return null

  if (name.endsWith('.csv')) {
    return 'Jackrabbit exports to Excel, not CSV — use Export to Excel rather than saving a copy.'
  }

  return 'Not a Jackrabbit export — these are .xls or .xlsx files.'
}

/**
 * When the Jackrabbit import last ran (D-647).
 *
 * Null until one has been applied. Everything in the app is only as current as
 * this, and until now nobody could tell - which is what the teachers asked
 * about: a roster from last month looks exactly like a roster from this
 * morning.
 */
export type LastImport = { at: string | null }

export const LAST_IMPORT_KEY = ['imports', 'last'] as const

export async function fetchLastImport(): Promise<LastImport> {
  const res = await fetch('/api/imports/last')
  if (!res.ok) throw new Error(`Could not read the last import (${res.status})`)
  return res.json()
}
